import { isPlaceholderArtifactSummary } from './chatArtifactTitle';

export interface ArtifactMergeLike {
  id?: string;
  kind?: string | null;
  summary?: string | null;
  size?: number;
  mimeType?: string;
  createdAt?: string;
  runId?: string;
  labels?: Record<string, string | undefined> | null;
  evidence?: unknown;
  isFinalAttachment?: boolean;
}

export const artifactIdentityKey = (artifactId?: string | null, runId?: string | null): string =>
  `${String(runId ?? '').trim()}::${String(artifactId ?? '').trim()}`;

const isGenericArtifactSummary = (summary?: string): boolean => {
  const normalized = summary?.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  if (normalized === 'artifact' || normalized === 'command output' || normalized === 'artifact ready') {
    return true;
  }
  if (/^artifact\s+[a-z0-9_-]+…$/i.test(summary?.trim() ?? '')) {
    return true;
  }
  return false;
};

const artifactSummaryQuality = (summary?: string): number => {
  const trimmed = summary?.trim();
  if (!trimmed) {
    return 0;
  }
  if (isPlaceholderArtifactSummary(trimmed)) {
    return 0;
  }
  if (isGenericArtifactSummary(trimmed)) {
    return 1;
  }
  return 2;
};

export const pickArtifactSummary = (preferred?: string, fallback?: string): string | undefined => {
  const preferredTrimmed = preferred?.trim();
  const fallbackTrimmed = fallback?.trim();
  const preferredQuality = artifactSummaryQuality(preferredTrimmed);
  const fallbackQuality = artifactSummaryQuality(fallbackTrimmed);

  if (preferredQuality > fallbackQuality) {
    return preferredTrimmed;
  }
  if (fallbackQuality > preferredQuality) {
    return fallbackTrimmed;
  }
  return preferredTrimmed || fallbackTrimmed || undefined;
};

export const mergeArtifactRecords = <T extends ArtifactMergeLike>(current: T | undefined, next: T): T => {
  if (!current) {
    return {
      ...next,
      summary: next.summary?.trim() || undefined
    };
  }

  const currentKind = String(current.kind ?? '').trim();
  const nextKind = String(next.kind ?? '').trim();
  const resolvedKind = currentKind.toLowerCase() === 'artifact' && nextKind ? nextKind : currentKind || nextKind;

  return {
    ...current,
    ...next,
    kind: resolvedKind || 'artifact',
    summary: pickArtifactSummary(next.summary ?? undefined, current.summary ?? undefined),
    size: next.size ?? current.size,
    mimeType: next.mimeType ?? current.mimeType,
    createdAt: next.createdAt ?? current.createdAt,
    runId: next.runId ?? current.runId,
    labels: current.labels || next.labels ? { ...(current.labels ?? {}), ...(next.labels ?? {}) } : undefined,
    evidence: next.evidence ?? current.evidence,
    isFinalAttachment: next.isFinalAttachment ?? current.isFinalAttachment
  };
};

export const findArtifactRecord = <T extends ArtifactMergeLike>(
  artifacts: T[],
  artifactId?: string | null,
  runId?: string | null
): T | undefined => {
  const normalizedArtifactId = String(artifactId ?? '').trim();
  if (!normalizedArtifactId) {
    return undefined;
  }

  const normalizedRunId = String(runId ?? '').trim();
  if (normalizedRunId) {
    const exact = artifacts.find(
      (artifact) => artifactIdentityKey(artifact.id, artifact.runId) === artifactIdentityKey(normalizedArtifactId, normalizedRunId)
    );
    if (exact) {
      return exact;
    }
  }

  const matches = artifacts.filter((artifact) => String(artifact.id ?? '').trim() === normalizedArtifactId);
  if (matches.length <= 1) {
    return matches[0];
  }

  return matches.find((artifact) => !String(artifact.runId ?? '').trim()) ?? matches[0];
};
